import styled, { css } from 'styled-components';
import {
  Align,
  BackgroundColor,
  BoxDisplay,
  Direction,
  FlexBoxDisplay,
  Justify,
  Position,
} from './types';
import { Radius, colors, radius } from '@ardoq/design-tokens';
import { ResponsiveStyle, mq } from './boxUtils';
import { listResetCss } from './listResetCss';

// This is a helper Component not intended for usage outside of style-helpers

type StyledBoxProps = {
  $paddingRight?: ResponsiveStyle;
  $paddingLeft?: ResponsiveStyle;
  $paddingBottom?: ResponsiveStyle;
  $paddingTop?: ResponsiveStyle;
  $marginRight?: ResponsiveStyle;
  $marginLeft?: ResponsiveStyle;
  $marginBottom?: ResponsiveStyle;
  $marginTop?: ResponsiveStyle;
  $borderRight?: string;
  $borderLeft?: string;
  $borderBottom?: string;
  $borderTop?: string;
  $display?: ResponsiveStyle<BoxDisplay | FlexBoxDisplay>;
  $isolate?: boolean;
  $position?: ResponsiveStyle<Position>;
  $justify?: ResponsiveStyle<Justify>;
  $align?: ResponsiveStyle<Align>;
  $gap?: ResponsiveStyle<string>;
  $flexWrap?: boolean;
  $flex?: ResponsiveStyle<React.CSSProperties['flex']>;
  $reverse?: boolean;
  $flexDirection?: ResponsiveStyle<Direction>;
  $flexGrow?: ResponsiveStyle<string>;
  $flexShrink?: ResponsiveStyle<string>;
  $width?: ResponsiveStyle;
  $height?: ResponsiveStyle;
  $backgroundColor?: BackgroundColor;
  $boxShadow?: string;
  $borderRadius?: Radius;
  $minWidth?: ResponsiveStyle;
  $maxWidth?: ResponsiveStyle;
  $minHeight?: ResponsiveStyle;
  $maxHeight?: ResponsiveStyle;
  $overflow?: string;
  $overflowX?: string;
  $overflowY?: string;
  $zIndex?: number;
  $transition?: string;
};

export const StyledBox = styled.div<StyledBoxProps>`
  ${listResetCss}

  ${props =>
    mq({
      marginTop: props.$marginTop,
      marginBottom: props.$marginBottom,
      marginLeft: props.$marginLeft,
      marginRight: props.$marginRight,
      paddingTop: props.$paddingTop,
      paddingBottom: props.$paddingBottom,
      paddingLeft: props.$paddingLeft,
      paddingRight: props.$paddingRight,

      display: props.$display,
      position: props.$position,
      width: props.$width,
      height: props.$height,
      justifyContent: props.$justify,
      alignItems: props.$align,
      gap: props.$gap,
      flex: props.$flex,
      flexGrow: props.$flexGrow,
      flexShrink: props.$flexShrink,
      flexDirection: props.$flexDirection,
      minWidth: props.$minWidth,
      maxWidth: props.$maxWidth,
      minHeight: props.$minHeight,
      maxHeight: props.$maxHeight,
    })}

  ${({ $borderTop }) =>
    $borderTop &&
    css`
      border-top: ${$borderTop};
    `}
  ${({ $borderBottom }) =>
    $borderBottom &&
    css`
      border-bottom: ${$borderBottom};
    `}
  ${({ $borderLeft }) =>
    $borderLeft &&
    css`
      border-left: ${$borderLeft};
    `}
  ${({ $borderRight }) =>
    $borderRight &&
    css`
      border-right: ${$borderRight};
    `}

  ${({ $isolate }) =>
    $isolate &&
    css`
      isolation: isolate;
    `}

  ${({ $flexWrap }) =>
    $flexWrap &&
    css`
      flex-wrap: wrap;
    `}
  ${({ $reverse }) =>
    $reverse &&
    css`
      flex-direction: row-reverse;
    `}
  ${({ $backgroundColor }) =>
    $backgroundColor &&
    css`
      background-color: ${colors[$backgroundColor]};
    `}
  ${({ $boxShadow }) =>
    $boxShadow &&
    css`
      box-shadow: ${$boxShadow};
    `}

  ${({ $borderRadius }) =>
    $borderRadius &&
    css`
      border-radius: ${radius[$borderRadius]};
    `}
  ${({ $overflow }) =>
    $overflow &&
    css`
      overflow: ${$overflow};
    `}
  ${({ $overflowX }) =>
    $overflowX &&
    css`
      overflow-x: ${$overflowX};
    `}
  ${({ $overflowY }) =>
    $overflowY &&
    css`
      overflow-y: ${$overflowY};
    `}
  ${({ $zIndex }) =>
    $zIndex &&
    css`
      z-index: ${$zIndex};
    `}
  ${({ $transition }) =>
    $transition &&
    css`
      transition: ${$transition};
    `}
`;
